import type { TBlockUserBody } from '@onesource/schemas';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

import axios from '../axios';
import { getBlockedUsersQueryKey, getBlockUserMutationKey, USER_BLOCKS_ENTITY } from './entity';

type TBlockResponse = { blockedUserIds: string[] };

const SNACKBAR_POSITION = { vertical: 'bottom', horizontal: 'center' } as const;

/**
 * Block state filters the community feed and mentions server-side, so refresh
 * the block list and every community-hub / mention query after a change.
 */
const useInvalidateBlockState = () => {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: getBlockedUsersQueryKey() }),
      qc.invalidateQueries({ queryKey: ['community-hub'] }),
      qc.invalidateQueries({ queryKey: ['mention'] }),
    ]);
};

export const useBlockUserMutation = () => {
  const invalidate = useInvalidateBlockState();

  return useMutation({
    mutationKey: getBlockUserMutationKey(),
    mutationFn: async (payload: TBlockUserBody) => {
      const res = await axios.post<TBlockResponse>(`/${USER_BLOCKS_ENTITY}`, payload);
      return res.data;
    },
    onSuccess: async () => {
      await invalidate();
      enqueueSnackbar('User blocked', { variant: 'success', anchorOrigin: SNACKBAR_POSITION });
    },
    onError: () => {
      enqueueSnackbar('Unable to block this user. Please try again.', {
        variant: 'error',
        anchorOrigin: SNACKBAR_POSITION,
      });
    },
  });
};

export const useUnblockUserMutation = () => {
  const invalidate = useInvalidateBlockState();

  return useMutation({
    mutationFn: async (authUserId: string) => {
      const res = await axios.delete<TBlockResponse>(`/${USER_BLOCKS_ENTITY}/${authUserId}`);
      return res.data;
    },
    onSuccess: async () => {
      await invalidate();
      enqueueSnackbar('User unblocked', { variant: 'success', anchorOrigin: SNACKBAR_POSITION });
    },
    onError: () => {
      enqueueSnackbar('Unable to unblock this user. Please try again.', {
        variant: 'error',
        anchorOrigin: SNACKBAR_POSITION,
      });
    },
  });
};
